/**
 * Sj3dBuilder — shared helpers for San Jose Thinkspace ring-builder enhancers.
 *
 * Internal San Jose (Ryan) · Dev theme only until promoted.
 *
 * Load order (see snippets/_3d-options-picker.liquid):
 *   1) this file
 *   2) sj-image-stepper.js
 *   3) ring / band / seal / metal / stone / engraving / gem enhancers
 *
 * Thinkspace host notes:
 *   - `x-3d-product-options.__w`  → controller (gems, metal, parts)
 *   - `x-3d-product-options.__wiz` → form wizard (setFieldValue)
 *   Exposed by SJ patch in thinkspacehq-3d-product-options.js
 */
(function (global) {
  'use strict';

  /** One debounced MutationObserver per widget (all enhancers share it). */
  var watches = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var FALLBACK_KEY = '_sj3dWatch';

  function widgetEl() {
    return (
      document.querySelector(
        'x-3d-product-options-custom-wrapper x-3d-product-options'
      ) || document.querySelector('x-3d-product-options')
    );
  }

  function stepperConfig() {
    return global.__SJ_IMAGE_STEPPER_CONFIG__ || {};
  }

  function gemConfig() {
    return global.__SJ_GEM_CONFIG__ || {};
  }

  /** Drive a Thinkspace <select> the way native change handlers expect. */
  function setSelectValue(select, value) {
    if (!select) return;
    if (select.value !== value) select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    select.dispatchEvent(new Event('input', { bubbles: true }));
  }

  var catalogCache = null;
  /** Parsed Thinkspace `data-prop="data"` JSON (addons, metals). Cached. */
  function catalogData() {
    if (catalogCache) return catalogCache;
    try {
      var script = document.querySelector(
        'x-3d-product-options script[data-prop="data"]'
      );
      catalogCache = script ? JSON.parse(script.textContent || '{}') : {};
    } catch (_) {
      catalogCache = {};
    }
    return catalogCache;
  }

  /** Coalesce bursts onto one animation frame. */
  function rafDebounce(fn) {
    var id = 0;
    return function () {
      if (id) return;
      id = global.requestAnimationFrame(function () {
        id = 0;
        fn();
      });
    };
  }

  function whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Run `init(widget)` once the Thinkspace host is in the DOM.
   * @param {(widget: Element) => void} init
   */
  function bootOnWidget(init) {
    whenReady(function () {
      var widget = widgetEl();
      if (!widget) return;
      init(widget);
    });
  }

  function getWatchEntry(widget) {
    if (watches) {
      var existing = watches.get(widget);
      if (existing) return existing;
    } else if (widget[FALLBACK_KEY]) {
      return widget[FALLBACK_KEY];
    }

    var entry = {
      cbs: [],
      timer: null,
      debounceMs: 80,
      mo: null,
      schedule: null,
    };

    function schedule() {
      if (entry.timer != null) return;
      entry.timer = global.setTimeout(function () {
        entry.timer = null;
        var list = entry.cbs.slice();
        for (var i = 0; i < list.length; i++) {
          try {
            list[i]();
          } catch (err) {
            if (typeof console !== 'undefined' && console.error) {
              console.error('[Sj3dBuilder]', err);
            }
          }
        }
      }, entry.debounceMs);
    }

    entry.schedule = schedule;
    entry.mo = new MutationObserver(schedule);
    entry.mo.observe(widget, { childList: true, subtree: true });
    widget.addEventListener('widget:loaded', schedule);

    if (watches) watches.set(widget, entry);
    else widget[FALLBACK_KEY] = entry;

    return entry;
  }

  /**
   * Debounced watch for Thinkspace re-renders / late-mounted fields.
   * Multiple enhancers share one observer on the widget (80ms coalesce).
   *
   * @param {Element} widget
   * @param {() => void} onChange
   * @param {{ debounceMs?: number }} [opts]
   */
  function watchWidget(widget, onChange, opts) {
    opts = opts || {};
    var entry = getWatchEntry(widget);
    if (opts.debounceMs != null && opts.debounceMs > entry.debounceMs) {
      entry.debounceMs = opts.debounceMs;
    }
    entry.cbs.push(onChange);
    onChange();
    return { schedule: entry.schedule, observer: entry.mo };
  }

  /** Controller (class H) — gems, colors, parts. */
  function controller() {
    var el = widgetEl();
    return (el && el.__w) || global.w || null;
  }

  /** Form wizard — setFieldValue for cart props. */
  function wizard() {
    var el = widgetEl();
    return (el && el.__wiz) || global.wiz || null;
  }

  global.Sj3dBuilder = {
    widgetEl: widgetEl,
    stepperConfig: stepperConfig,
    gemConfig: gemConfig,
    catalogData: catalogData,
    rafDebounce: rafDebounce,
    setSelectValue: setSelectValue,
    whenReady: whenReady,
    bootOnWidget: bootOnWidget,
    watchWidget: watchWidget,
    controller: controller,
    wizard: wizard,
  };

  function isPhoneLayout() {
    try {
      return window.matchMedia('(max-width: 767px)').matches;
    } catch (err) {
      return false;
    }
  }

  /** Sit the mobile Back/Next + progress chrome on the 3D ring, not under the gallery. */
  function pinMobileStepChrome() {
    if (!isPhoneLayout()) return;
    var left = document.querySelector(
      '.main-section--product[data-is-3d-product="true"] .product__primary-left'
    );
    if (!left) return;
    var portal = left.querySelector('[data-portals-container="x-3d-product-options"]');
    if (!portal) return;
    var media =
      left.querySelector('.product__media-item') ||
      left.querySelector('.product__media-gallery');
    if (!media || portal.parentElement === media) return;
    media.appendChild(portal);
  }

  function currentStepId() {
    var wiz = wizard();
    var step = wiz && wiz.currentStep;
    return String((step && step.id) || '');
  }

  function isEngravingStep() {
    if (currentStepId() === 'Other') return true;
    var panel = document.querySelector('.sj-engraving-panel');
    if (!panel) return false;
    return panel.offsetParent !== null || panel.getClientRects().length > 0;
  }

  function findPersonalizeSource() {
    var imgs = document.querySelectorAll(
      '.main-section--product[data-is-3d-product="true"] .product__primary-right .product-block-image img[src*="ATTENTIVE_BANNERS.jpg"]'
    );
    var i;
    for (i = 0; i < imgs.length; i++) {
      var block = imgs[i].closest('.product-block-image');
      if (!block || block.classList.contains('sj-personalize-graphic-mobile')) continue;
      block.setAttribute('data-sj-personalize-source', '');
      return block;
    }
    return null;
  }

  function engravingAnchor() {
    var panel = document.querySelector('.sj-engraving-panel');
    if (panel) return panel;
    var fields = document.querySelectorAll('[data-option="Two-tone"]');
    var i;
    for (i = 0; i < fields.length; i++) {
      var text = String(fields[i].textContent || '').toLowerCase();
      if (text.indexOf('engrav') === -1) continue;
      return (
        fields[i].closest('.sj-engraving-panel') ||
        fields[i].parentElement ||
        fields[i]
      );
    }
    return null;
  }

  /** Hide “Personalize your ring” on phones; show it under Add Engraving on that step. */
  function pinMobilePersonalizeGraphic() {
    var source = findPersonalizeSource();
    var clone = document.querySelector('.sj-personalize-graphic-mobile');
    var onEngraving = isPhoneLayout() && isEngravingStep();
    var anchor = onEngraving ? engravingAnchor() : null;

    if (!onEngraving || !anchor || !source) {
      if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
      return;
    }

    if (!clone) {
      clone = source.cloneNode(true);
      clone.removeAttribute('data-sj-personalize-source');
      clone.classList.add('sj-personalize-graphic-mobile');
      clone.querySelectorAll('[id]').forEach(function (el) {
        el.removeAttribute('id');
      });
    }

    if (clone.parentElement !== anchor.parentElement || clone.previousElementSibling !== anchor) {
      if (anchor.nextSibling) {
        anchor.parentNode.insertBefore(clone, anchor.nextSibling);
      } else {
        anchor.parentNode.appendChild(clone);
      }
    }
  }

  function reviewsHome(panel) {
    var existing = document.querySelector('[data-sj-reviews-home]');
    if (existing) return existing;
    if (!panel || !panel.parentNode) return null;
    var mark = document.createElement('span');
    mark.setAttribute('data-sj-reviews-home', '');
    mark.hidden = true;
    panel.parentNode.insertBefore(mark, panel);
    return mark;
  }

  /** On phones, sit Customer photos & reviews above the shipping / help box. */
  function pinMobileReviews() {
    var root = document.querySelector(
      '.main-section--product[data-is-3d-product="true"]'
    );
    if (!root) return;
    var callouts = root.querySelector('.product__block-callouts');
    var panel =
      root.querySelector('.sj-reviews-panel.sj-reviews-panel--above-callouts') ||
      root.querySelector('.product__bottom .sj-reviews-panel') ||
      root.querySelector('.left-side-blocks.for-mobile .sj-reviews-panel');
    if (!panel || !callouts) return;

    var home = reviewsHome(panel);

    if (isPhoneLayout()) {
      panel.classList.add('sj-reviews-panel--above-callouts');
      if (panel.nextElementSibling !== callouts) {
        callouts.parentNode.insertBefore(panel, callouts);
      }
      return;
    }

    panel.classList.remove('sj-reviews-panel--above-callouts');
    if (home && home.parentNode && panel.previousElementSibling !== home) {
      home.parentNode.insertBefore(panel, home.nextSibling);
    }
  }

  var STEP_LABELS = {
    Seal: 'Top & Seal',
    Band: 'Band',
    Metal: 'Metal',
    Stone: 'Stone',
    Other: 'Engraving',
    Review: 'Review',
  };

  function pinOverlayStepLabel() {
    var root = document.querySelector(
      '.main-section--product[data-is-3d-product="true"]'
    );
    if (!root) return;
    var id = currentStepId();
    if (id) root.setAttribute('data-sj-overlay-step', id);
    else root.removeAttribute('data-sj-overlay-step');

    var label = root.querySelector(
      '[data-portals-container-content] > .tw\\:flex > span.tw\\:flex-grow > span > span:first-child'
    );
    if (!label) return;
    var pretty = STEP_LABELS[id] || id;
    if (
      id === 'Other' &&
      document.querySelector('[data-option="Chain Metal"]')
    ) {
      pretty = 'Chain';
    }
    if (pretty && label.textContent !== pretty) label.textContent = pretty;
  }

  var layoutTimer = null;
  function pinMobileLayout() {
    pinMobileStepChrome();
    pinMobilePersonalizeGraphic();
    pinMobileReviews();
    pinOverlayStepLabel();
  }
  function scheduleMobileLayout() {
    if (layoutTimer) window.clearTimeout(layoutTimer);
    layoutTimer = window.setTimeout(pinMobileLayout, 80);
  }

  function bootMobileLayout() {
    pinMobileLayout();
    var root = document.querySelector(
      '.main-section--product[data-is-3d-product="true"]'
    );
    if (!root || root.getAttribute('data-sj-mobile-layout') === '1') return;
    root.setAttribute('data-sj-mobile-layout', '1');
    window.addEventListener('resize', scheduleMobileLayout, { passive: true });
    bootOnWidget(function (widget) {
      watchWidget(widget, scheduleMobileLayout);
      widget.addEventListener('data:change', scheduleMobileLayout);
    });
    // Reviews / personalize live in the right column, not the Thinkspace widget.
    var right = root.querySelector('.product__primary-right');
    if (right) {
      new MutationObserver(scheduleMobileLayout).observe(right, {
        childList: true,
        subtree: false,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootMobileLayout);
  } else {
    bootMobileLayout();
  }
})(window);
